import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import {
  User,
  Shield,
  AlertCircle,
  Save,
  Edit2,
  X,
  Plus,
  Trash2,
  Code,
  Upload,
  Camera
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import AdminSection from '../components/AdminSection';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

// ─── Skill options ─────────────────────────────────────────────────────────────

const skillOptions = [
  { value: 'robot_programming',  label: 'Robot Programming',              category: 'Robotics'    },
  { value: 'robot_vision',       label: 'Robot Vision Systems',           category: 'Robotics'    },
  { value: 'robot_kinematics',   label: 'Robot Kinematics',               category: 'Robotics'    },
  { value: 'robot_control',      label: 'Robot Control Systems',          category: 'Robotics'    },
  { value: 'robot_simulation',   label: 'Robot Simulation',               category: 'Robotics'    },
  { value: 'ros',                label: 'ROS (Robot Operating System)',   category: 'Robotics'    },
  { value: 'drone_development',  label: 'Drone Development',              category: 'Robotics'    },
  { value: 'cad_design',         label: 'CAD Design',                     category: '3D'          },
  { value: '3d_modeling',        label: '3D Modeling',                    category: '3D'          },
  { value: '3d_printing',        label: '3D Printing',                    category: '3D'          },
  { value: 'blender',            label: 'Blender',                        category: '3D'          },
  { value: 'unity3d',            label: 'Unity 3D',                       category: '3D'          },
  { value: 'unreal_engine',      label: 'Unreal Engine',                  category: '3D'          },
  { value: 'ar_vr',              label: 'AR/VR Development',              category: '3D'          },
  { value: 'embedded_systems',   label: 'Embedded Systems',               category: 'IoT'         },
  { value: 'sensor_integration', label: 'Sensor Integration',             category: 'IoT'         },
  { value: 'microcontrollers',   label: 'Microcontrollers (Arduino, ESP32)', category: 'IoT'      },
  { value: 'iot_protocols',      label: 'IoT Protocols (MQTT, CoAP)',     category: 'IoT'         },
  { value: 'raspberry_pi',       label: 'Raspberry Pi',                   category: 'IoT'         },
  { value: 'pcb_design',         label: 'PCB Design',                     category: 'IoT'         },
  { value: 'hardware_prototyping', label: 'Hardware Prototyping',         category: 'IoT'         },
  { value: 'web_frontend',       label: 'Web Frontend Development',       category: 'Software'    },
  { value: 'web_backend',        label: 'Web Backend Development',        category: 'Software'    },
  { value: 'mobile_dev',         label: 'Mobile App Development',         category: 'Software'    },
  { value: 'python',             label: 'Python',                         category: 'Software'    },
  { value: 'javascript',         label: 'JavaScript',                     category: 'Software'    },
  { value: 'cpp',                label: 'C/C++',                          category: 'Software'    },
  { value: 'machine_learning',   label: 'Machine Learning',               category: 'Software'    },
  { value: 'computer_vision',    label: 'Computer Vision',                category: 'Software'    },
  { value: 'cloud_services',     label: 'Cloud Services',                 category: 'Software'    },
  { value: 'devops',             label: 'DevOps',                         category: 'Software'    },
  { value: 'database',           label: 'Database Management',            category: 'Software'    },
  { value: 'media_relations',    label: 'Media Relations',                category: 'PR'          },
  { value: 'crisis_management',  label: 'Crisis Management',              category: 'PR'          },
  { value: 'press_releases',     label: 'Press Release Writing',          category: 'PR'          },
  { value: 'public_speaking',    label: 'Public Speaking',                category: 'PR'          },
  { value: 'event_planning',     label: 'Event Planning & Coordination',  category: 'PR'          },
  { value: 'community_engagement', label: 'Community Engagement',         category: 'PR'          },
  { value: 'stakeholder_relations', label: 'Stakeholder Relations',       category: 'PR'          },
  { value: 'brand_reputation',   label: 'Brand Reputation Management',    category: 'PR'          },
  { value: 'digital_marketing',  label: 'Digital Marketing',              category: 'Marketing'   },
  { value: 'social_media',       label: 'Social Media Marketing',         category: 'Marketing'   },
  { value: 'content_creation',   label: 'Content Creation',               category: 'Marketing'   },
  { value: 'seo',                label: 'SEO (Search Engine Optimization)', category: 'Marketing' },
  { value: 'email_marketing',    label: 'Email Marketing',                category: 'Marketing'   },
  { value: 'market_research',    label: 'Market Research & Analysis',     category: 'Marketing'   },
  { value: 'branding',           label: 'Branding Strategy',              category: 'Marketing'   },
  { value: 'analytics',          label: 'Marketing Analytics',            category: 'Marketing'   },
  { value: 'campaign_management', label: 'Campaign Management',           category: 'Marketing'   },
  { value: 'photoshop',          label: 'Adobe Photoshop',                category: 'Graphics'    },
  { value: 'capcut',             label: 'CapCut',                         category: 'Graphics'    },
  { value: 'illustrator',        label: 'Adobe Illustrator',              category: 'Graphics'    },
  { value: 'indesign',           label: 'Adobe InDesign',                 category: 'Graphics'    },
  { value: 'after_effects',      label: 'Adobe After Effects',            category: 'Graphics'    },
  { value: 'premiere_pro',       label: 'Adobe Premiere Pro',             category: 'Graphics'    },
  { value: 'graphic_design',     label: 'Graphic Design',                 category: 'Graphics'    },
  { value: 'ui_design',          label: 'UI Design',                      category: 'Graphics'    },
  { value: 'motion_graphics',    label: 'Motion Graphics',                category: 'Graphics'    },
  { value: 'video_editing',      label: 'Video Editing',                  category: 'Graphics'    },
  { value: 'photography',        label: 'Photography',                    category: 'Graphics'    },
  { value: 'team_management',    label: 'Team Management',                category: 'Leadership'  },
  { value: 'project_management', label: 'Project Management',             category: 'Leadership'  },
  { value: 'strategic_planning', label: 'Strategic Planning',             category: 'Leadership'  },
  { value: 'decision_making',    label: 'Decision Making',                category: 'Leadership'  },
  { value: 'conflict_resolution', label: 'Conflict Resolution',           category: 'Leadership'  },
  { value: 'delegation',         label: 'Effective Delegation',           category: 'Leadership'  },
  { value: 'mentoring',          label: 'Mentoring & Coaching',           category: 'Leadership'  },
  { value: 'change_management',  label: 'Change Management',              category: 'Leadership'  },
  { value: 'emotional_intelligence', label: 'Emotional Intelligence',     category: 'Leadership'  },
  { value: 'languages',          label: 'Foreign Languages',              category: 'Other'       },
  { value: 'technical_writing',  label: 'Technical Writing',              category: 'Other'       },
  { value: 'presentation',       label: 'Presentation Skills',            category: 'Other'       },
  { value: 'research',           label: 'Research Methods',               category: 'Other'       },
  { value: 'data_analysis',      label: 'Data Analysis',                  category: 'Other'       },
  { value: 'critical_thinking',  label: 'Critical Thinking',              category: 'Other'       },
  { value: 'time_management',    label: 'Time Management',                category: 'Other'       },
  { value: 'problem_solving',    label: 'Problem Solving',                category: 'Other'       },
  { value: 'collaboration',      label: 'Collaboration & Teamwork',       category: 'Other'       },
];

// ─── Shared select styles (brand palette) ─────────────────────────────────────

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: '0.75rem',
    border: `1px solid ${SYSTEM_COLORS.borderSoft}`,
    boxShadow: 'none',
    backgroundColor: SYSTEM_COLORS.shellSurface,
    '&:hover': { border: `1px solid ${BRAND_COLORS.primary}` },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? BRAND_COLORS.primary : state.isFocused ? SYSTEM_COLORS.primaryTint : null,
    color: state.isSelected ? '#fff' : NEUTRAL_COLORS.black,
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontWeight: 'bold',
    fontSize: '0.75rem',
    color: SYSTEM_COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: SYSTEM_COLORS.surfaceSubtle,
    padding: '0.5rem 1rem',
  }),
};

// ─── ProficiencyBadge ─────────────────────────────────────────────────────────

const proficiencyStyle = {
  beginner:     { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info    },
  intermediate: { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success },
  advanced:     { backgroundColor: '#f3e8ff',                 color: '#7e22ce'             },
  expert:       { backgroundColor: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger  },
};

const ProficiencyBadge = ({ proficiency }) => (
  <span
    className="text-xs px-2 py-0.5 rounded-full font-semibold"
    style={proficiencyStyle[proficiency] || proficiencyStyle.beginner}
  >
    {proficiency.charAt(0).toUpperCase() + proficiency.slice(1)}
  </span>
);

// ─── ProfilePictureModal ──────────────────────────────────────────────────────

const ProfilePictureModal = ({ isOpen, onClose, onUpload }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedFile && imageRef.current && containerRef.current) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      const img = new Image();
      img.onload = () => {
        const imageAspect = img.width / img.height;
        let initialZoom = 1;
        if (imageAspect < 0.8) initialZoom = 1.3;
        else if (imageAspect > 1.5) initialZoom = 1.2;
        setZoom(initialZoom);
        setCrop({ x: 0, y: imageAspect < 1 ? -20 : 0 });
      };
      img.src = URL.createObjectURL(selectedFile);
    }
  }, [selectedFile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) { setSelectedFile(null); return; }
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); setSelectedFile(null); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size should be less than 5MB'); setSelectedFile(null); return; }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) { toast.error('Please select an image'); return; }
    try {
      setLoading(true);
      const processedFile = await processImage(selectedFile, crop, zoom);
      await onUpload(processedFile);
      setLoading(false);
    } catch (error) {
      toast.error(error.message || 'Failed to upload image');
      setLoading(false);
    }
  };

  const processImage = (file, crop, zoom) =>
    new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const outputSize = 500;
          canvas.width = outputSize;
          canvas.height = outputSize;
          const ctx = canvas.getContext('2d');
          ctx.beginPath();
          ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, outputSize, outputSize);
          const containerSize = 256;
          const imageAspect = img.width / img.height;
          let displayWidth, displayHeight;
          if (imageAspect > 1) { displayHeight = containerSize; displayWidth = displayHeight * imageAspect; }
          else { displayWidth = containerSize; displayHeight = displayWidth / imageAspect; }
          const scaleX = img.width / displayWidth;
          const scaleY = img.height / displayHeight;
          const offsetX = (containerSize - displayWidth) / 2;
          const offsetY = (containerSize - displayHeight) / 2;
          const visibleCenterX = containerSize / 2 - crop.x;
          const visibleCenterY = containerSize / 2 - crop.y;
          let imageCenterX = (visibleCenterX - offsetX) * scaleX;
          let imageCenterY = (visibleCenterY - offsetY) * scaleY;
          const cropSizeInImage = (containerSize / zoom) * scaleX;
          let sourceX = Math.max(0, imageCenterX - cropSizeInImage / 2);
          let sourceY = Math.max(0, imageCenterY - cropSizeInImage / 2);
          let sourceWidth = Math.min(img.width - sourceX, cropSizeInImage);
          let sourceHeight = Math.min(img.height - sourceY, cropSizeInImage);
          const sourceDimension = Math.min(sourceWidth, sourceHeight);
          sourceWidth = sourceDimension;
          sourceHeight = sourceDimension;
          sourceX = Math.max(0, imageCenterX - sourceWidth / 2);
          sourceY = Math.max(0, imageCenterY - sourceHeight / 2);
          if (sourceX + sourceWidth > img.width) sourceX = img.width - sourceWidth;
          if (sourceY + sourceHeight > img.height) sourceY = img.height - sourceHeight;
          ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, outputSize, outputSize);
          canvas.toBlob((blob) => {
            if (blob) resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
            else reject(new Error('Failed to process image'));
          }, 'image/jpeg', 0.95);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
      } catch (error) { reject(error); }
    });

  const handleClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleWheel = useCallback((e) => {
    if (!selectedFile) return;
    e.preventDefault();
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const mouseRelativeX = mouseX - crop.x;
    const mouseRelativeY = mouseY - crop.y;
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    const newZoom = Math.max(1, Math.min(3, zoom + delta));
    const zoomChange = newZoom / zoom;
    if (Math.abs(newZoom - zoom) > 0.01) {
      setZoom(newZoom);
      setCrop({ x: mouseX - mouseRelativeX * zoomChange, y: mouseY - mouseRelativeY * zoomChange });
    }
  }, [selectedFile, crop, zoom]);

  const handleMouseDown = useCallback((e) => {
    if (!selectedFile) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - crop.x, y: e.clientY - crop.y });
  }, [selectedFile, crop]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setCrop({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => { setIsDragging(false); }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length !== 2) return;
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    containerRef.current.dataset.initialPinchDistance = d;
    containerRef.current.dataset.initialZoom = zoom;
  }, [zoom]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length !== 2 || !containerRef.current.dataset.initialPinchDistance) return;
    const d = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    const init = parseFloat(containerRef.current.dataset.initialPinchDistance);
    const initZoom = parseFloat(containerRef.current.dataset.initialZoom);
    if (d && init) setZoom(Math.max(1, Math.min(3, initZoom * (d / init))));
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (containerRef.current) {
      delete containerRef.current.dataset.initialPinchDistance;
      delete containerRef.current.dataset.initialZoom;
    }
  }, []);

  const handleTouchStart2 = useCallback((e) => {
    if (!selectedFile || e.touches.length !== 1) return;
    setIsDragging(true);
    setDragStart({ x: e.touches[0].clientX - crop.x, y: e.touches[0].clientY - crop.y });
  }, [selectedFile, crop]);

  const handleTouchMove2 = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    setCrop({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y });
  }, [isDragging, dragStart]);

  const handleTouchEnd2 = useCallback(() => { setIsDragging(false); }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !selectedFile) return;
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchstart', handleTouchStart2);
    container.addEventListener('touchmove', handleTouchMove2);
    container.addEventListener('touchend', handleTouchEnd2);
    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchstart', handleTouchStart2);
      container.removeEventListener('touchmove', handleTouchMove2);
      container.removeEventListener('touchend', handleTouchEnd2);
    };
  }, [selectedFile, isDragging, dragStart, crop, zoom,
    handleWheel, handleMouseDown, handleMouseMove, handleMouseUp,
    handleTouchStart, handleTouchMove, handleTouchEnd,
    handleTouchStart2, handleTouchMove2, handleTouchEnd2]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 shadow-lg"
        style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}
      >
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Update Profile Picture</h3>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-lg hover:opacity-70 transition-opacity"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          {selectedFile ? (
            <div className="flex flex-col items-center">
              <div
                ref={containerRef}
                className="relative w-64 h-64 rounded-full overflow-hidden mb-4 cursor-move"
                style={{ border: `2px solid ${SYSTEM_COLORS.borderSoft}`, touchAction: 'none' }}
              >
                <div
                  ref={imageRef}
                  className="absolute inset-0 transition-transform duration-100"
                  style={{
                    transform: `translate(${crop.x}px, ${crop.y}px) scale(${zoom})`,
                    backgroundImage: `url(${URL.createObjectURL(selectedFile)})`,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    height: '100%',
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-full h-full border-4 border-white rounded-full opacity-60" />
                </div>
              </div>

              <div className="w-full space-y-3">
                <p className="text-xs text-center" style={{ color: SYSTEM_COLORS.textMuted }}>{selectedFile.name}</p>

                <div
                  className="rounded-xl p-3 text-xs"
                  style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                >
                  <p className="font-semibold mb-1">How to use:</p>
                  <ul className="ml-4 list-disc space-y-0.5">
                    <li>Drag the image to position it</li>
                    <li>Use mouse wheel to zoom in/out</li>
                    <li>On mobile, pinch to zoom and drag to position</li>
                  </ul>
                </div>

                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: SYSTEM_COLORS.textMuted }}>Zoom</span>
                    <span style={{ color: NEUTRAL_COLORS.black }}>{Math.round(zoom * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-full h-1.5 rounded-lg appearance-none cursor-pointer"
                    style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}
                  />
                </div>

                <button
                  onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="text-xs w-full hover:opacity-70 transition-opacity"
                  style={{ color: SYSTEM_COLORS.danger }}
                >
                  Remove Selected Image
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer border-2 border-dashed hover:opacity-80 transition-opacity"
              style={{ borderColor: SYSTEM_COLORS.border, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
            >
              <Upload className="w-10 h-10 mb-2" style={{ color: SYSTEM_COLORS.textSoft }} />
              <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>Click to select an image</p>
              <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>PNG, JPG, JPEG up to 5MB</p>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
            </div>
          )}

          <div className="flex justify-end gap-3 mt-2">
            <button
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold border hover:opacity-70 transition-opacity"
              style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted, backgroundColor: SYSTEM_COLORS.shellSurface }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || loading}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
              style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
            >
              {loading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── SettingsPage ─────────────────────────────────────────────────────────────

const SettingsPage = () => {
  const { user, updateProfile, changePassword } = useApiAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isProfilePictureModalOpen, setIsProfilePictureModalOpen] = useState(false);
  const [profilePhotoURL, setProfilePhotoURL] = useState(user?.photo_url || user?.photoURL || null);

  useEffect(() => {
    const url = user?.photo_url || user?.photoURL;
    if (url) setProfilePhotoURL(url);
  }, [user]);

  const nameInputRef  = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const bioInputRef   = useRef(null);

  const normalizeSkills = (raw = []) =>
    raw.map(s =>
      typeof s === 'string'
        ? { id: s, value: s, name: s, proficiency: 'beginner', category: '' }
        : s
    );

  const [profileData, setProfileData] = useState({
    name:   user?.name  || '',
    email:  user?.email || '',
    phone:  user?.phone || '',
    bio:    user?.bio   || '',
    skills: normalizeSkills(user?.skills),
  });

  const [securityData, setSecurityData] = useState({
    currentPassword:  '',
    newPassword:      '',
    confirmPassword:  '',
    twoFactorEnabled: user?.twoFactorEnabled || false,
  });

  const [newSkill, setNewSkill]           = useState({ name: '', value: '', proficiency: 'beginner' });
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const normalizeCategory = (cat) => {
    if (!cat) return '';
    return cat.toLowerCase() === '3d' ? '3d' : cat.toLowerCase();
  };

  const handleCategorySelect = (opt) => {
    setSelectedCategory(opt);
    setSelectedSkill(null);
    setNewSkill({ name: '', value: '', proficiency: 'beginner' });
  };

  const categoryOptions = [
    { value: 'robotics',  label: 'Robotics'                 },
    { value: '3d',        label: '3D'                       },
    { value: 'iot',       label: 'IoT'                      },
    { value: 'software',  label: 'Software Development'     },
    { value: 'pr',        label: 'Public Relations (PR)'    },
    { value: 'marketing', label: 'Marketing'                },
    { value: 'graphics',  label: 'Graphics'                 },
    { value: 'leadership', label: 'Leadership'              },
    { value: 'other',     label: 'Other Skills'             },
  ];

  const handleAddSkill = () => {
    if (!newSkill.name) { toast.error('Please select a skill'); return; }
    if (profileData.skills.some(s => s.value === newSkill.value || s.name.toLowerCase() === newSkill.name.toLowerCase())) {
      toast.error('This skill already exists in your profile');
      return;
    }
    setProfileData(prev => ({
      ...prev,
      skills: [...prev.skills, { id: Date.now().toString(), value: newSkill.value, name: newSkill.name, proficiency: newSkill.proficiency, category: selectedCategory.value }],
    }));
    toast.success(`${newSkill.name} skill added`);
    setNewSkill({ name: '', value: '', proficiency: 'beginner' });
    setSelectedSkill(null);
    setSelectedCategory(null);
  };

  const handleRemoveSkill = (id) => {
    setProfileData(prev => ({ ...prev, skills: prev.skills.filter(s => s.id !== id) }));
  };

  const handleUpdateSkillProficiency = (id, proficiency) => {
    setProfileData(prev => ({ ...prev, skills: prev.skills.map(s => s.id === id ? { ...s, proficiency } : s) }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const updated = await updateProfile({
        name:   profileData.name,
        phone:  profileData.phone,
        skills: profileData.skills.map(s => s.name || s.value || s),
      });
      if (updated) {
        setProfileData(prev => ({
          ...prev,
          skills: normalizeSkills(updated.skills),
        }));
      }
      setIsEditing(false);
    } catch { /* errors surface via toast in updateProfile */ }
    finally { setLoading(false); }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfilePictureUpload = async (file) => {
    if (!file) { toast.error('Please select a profile picture'); return; }
    try {
      setLoading(true);
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = e => res(e.target.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const updated = await updateProfile({ photo_url: dataUrl });
      const newUrl = updated?.photo_url || dataUrl;
      setProfilePhotoURL(newUrl);
      setIsProfilePictureModalOpen(false);
      return newUrl;
    } catch (error) {
      toast.error('Failed to update profile picture');
      throw error;
    } finally { setLoading(false); }
  };

  // ── Input / textarea shared style ──────────────────────────────────────────
  const inputCls = 'w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-colors disabled:opacity-60';
  const inputStyle = (disabled) => ({
    borderColor: SYSTEM_COLORS.borderSoft,
    backgroundColor: disabled ? SYSTEM_COLORS.surfaceSubtle : SYSTEM_COLORS.shellSurface,
    color: NEUTRAL_COLORS.black,
  });
  const labelCls = 'block text-xs font-semibold mb-1';

  // ── Profile tab ────────────────────────────────────────────────────────────
  const renderProfileSettings = () => (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Profile Information</p>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="inline-flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity"
          style={{ color: BRAND_COLORS.primary }}
        >
          {isEditing ? <><X size={15} /> Cancel</> : <><Edit2 size={15} /> Edit</>}
        </button>
      </div>

      {/* Profile picture */}
      <div className="flex items-center gap-4">
        <div className="relative group">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}
          >
            {profilePhotoURL
              ? <img
                  src={profilePhotoURL.startsWith('data:') ? profilePhotoURL : `${profilePhotoURL}?t=${Date.now()}`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  key={profilePhotoURL}
                />
              : <User size={30} style={{ color: SYSTEM_COLORS.textSoft }} />
            }
          </div>
          {isEditing && (
            <button
              onClick={() => setIsProfilePictureModalOpen(true)}
              className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center"
              style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
            >
              <Camera size={13} />
            </button>
          )}
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            {user?.displayName || user?.name || 'User'}
          </p>
          <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{user?.email}</p>
          {isEditing && (
            <button
              onClick={() => setIsProfilePictureModalOpen(true)}
              className="text-xs mt-1 hover:opacity-70 transition-opacity"
              style={{ color: BRAND_COLORS.primary }}
            >
              Change Picture
            </button>
          )}
        </div>
      </div>

      {/* Fields */}
      {[
        { field: 'name',  label: 'Full Name',    type: 'text',  ref: nameInputRef  },
        { field: 'email', label: 'Email Address', type: 'email', ref: emailInputRef },
        { field: 'phone', label: 'Phone Number',  type: 'tel',   ref: phoneInputRef },
      ].map(({ field, label, type, ref }) => (
        <div key={field}>
          <label className={labelCls} style={{ color: SYSTEM_COLORS.textMuted }}>{label}</label>
          <input
            ref={ref}
            type={type}
            value={profileData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            disabled={!isEditing}
            className={inputCls}
            style={inputStyle(!isEditing)}
          />
        </div>
      ))}

      <div>
        <label className={labelCls} style={{ color: SYSTEM_COLORS.textMuted }}>Bio</label>
        <textarea
          ref={bioInputRef}
          value={profileData.bio}
          onChange={(e) => handleInputChange('bio', e.target.value)}
          disabled={!isEditing}
          rows={3}
          className={inputCls}
          style={inputStyle(!isEditing)}
        />
      </div>

      {/* Skills */}
      <div
        className="rounded-xl border p-4 space-y-3"
        style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
      >
        <div className="flex justify-between items-center">
          <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Skills</p>
          <div className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            <Code size={12} />
            {profileData.skills.length} {profileData.skills.length === 1 ? 'skill' : 'skills'}
          </div>
        </div>

        {profileData.skills.length === 0 && !isEditing ? (
          <div className="text-center py-6">
            <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No skills added yet</p>
            <button
              onClick={() => setIsEditing(true)}
              className="text-xs mt-1 hover:opacity-70"
              style={{ color: BRAND_COLORS.primary }}
            >
              Edit profile to add skills
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {profileData.skills.map(skill => (
              <div
                key={skill.id}
                className="flex items-center justify-between p-3 rounded-xl border"
                style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
              >
                <div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{skill.name}</p>
                    {skill.category && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                      >
                        {skill.category.charAt(0).toUpperCase() + skill.category.slice(1)}
                      </span>
                    )}
                  </div>
                  <div className="mt-1">
                    {isEditing ? (
                      <select
                        value={skill.proficiency}
                        onChange={(e) => handleUpdateSkillProficiency(skill.id, e.target.value)}
                        className="text-xs rounded-lg border px-1.5 py-0.5 outline-none"
                        style={{ borderColor: SYSTEM_COLORS.borderSoft, color: NEUTRAL_COLORS.black, backgroundColor: SYSTEM_COLORS.shellSurface }}
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                        <option value="expert">Expert</option>
                      </select>
                    ) : (
                      <ProficiencyBadge proficiency={skill.proficiency} />
                    )}
                  </div>
                </div>
                {isEditing && (
                  <button
                    onClick={() => handleRemoveSkill(skill.id)}
                    className="ml-2 hover:opacity-70 transition-opacity"
                    style={{ color: SYSTEM_COLORS.danger }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {isEditing && (
          <div
            className="rounded-xl border p-3 space-y-3"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
          >
            <p className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Add New Skill</p>
            <div>
              <label className="block text-xs mb-1" style={{ color: SYSTEM_COLORS.textMuted }}>Select Category</label>
              <Select
                placeholder="Select a category…"
                options={categoryOptions}
                styles={customSelectStyles}
                value={selectedCategory}
                onChange={handleCategorySelect}
              />
            </div>

            {selectedCategory && (
              <div>
                <label className="block text-xs mb-1" style={{ color: SYSTEM_COLORS.textMuted }}>
                  Select Skill in {selectedCategory.label}
                </label>
                <Select
                  placeholder={`Select a ${selectedCategory.label.toLowerCase()} skill…`}
                  options={skillOptions.filter(s => normalizeCategory(s.category) === selectedCategory.value)}
                  styles={customSelectStyles}
                  value={selectedSkill}
                  onChange={(selected) => {
                    if (selected) {
                      setSelectedSkill(selected);
                      setNewSkill({ ...newSkill, name: selected.label, value: selected.value });
                    }
                  }}
                  isSearchable
                />
              </div>
            )}

            <div className="flex gap-2">
              <select
                value={newSkill.proficiency}
                onChange={(e) => setNewSkill({ ...newSkill, proficiency: e.target.value })}
                disabled={!selectedSkill}
                className="flex-1 rounded-xl border px-2 py-2 text-xs outline-none disabled:opacity-50"
                style={{ borderColor: SYSTEM_COLORS.borderSoft, color: NEUTRAL_COLORS.black, backgroundColor: SYSTEM_COLORS.shellSurface }}
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </select>
              <button
                type="button"
                onClick={handleAddSkill}
                disabled={!newSkill.name}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
              >
                <Plus size={13} /> Add Skill
              </button>
            </div>
          </div>
        )}
      </div>

      {isEditing && (
        <div className="flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
          >
            <Save size={15} />
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );

  // ── Security tab ────────────────────────────────────────────────────────────
  const SecuritySettings = () => {
    const currentPasswordRef  = useRef(null);
    const newPasswordRef      = useRef(null);
    const confirmPasswordRef  = useRef(null);

    const [localSecurityData, setLocalSecurityData] = useState({
      currentPassword: securityData.currentPassword,
      newPassword:     securityData.newPassword,
      confirmPassword: securityData.confirmPassword,
    });
    const [localLoading, setLocalLoading] = useState(false);

    useEffect(() => {
      if (!localLoading) setLocalSecurityData({ currentPassword: securityData.currentPassword, newPassword: securityData.newPassword, confirmPassword: securityData.confirmPassword });
    }, [localLoading]);

    const handleSecurityInputChange = (field, value) => {
      setLocalSecurityData(prev => ({ ...prev, [field]: value }));
    };

    const handleLocalPasswordChange = async () => {
      if (!localSecurityData.currentPassword) { toast.error('Please enter your current password'); currentPasswordRef.current?.focus(); return; }
      if (!localSecurityData.newPassword)     { toast.error('Please enter a new password');        newPasswordRef.current?.focus();     return; }
      if (localSecurityData.newPassword !== localSecurityData.confirmPassword) { toast.error('New passwords do not match'); confirmPasswordRef.current?.focus(); return; }
      try {
        setLocalLoading(true);
        await changePassword(localSecurityData.currentPassword, localSecurityData.newPassword);
        setLocalSecurityData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSecurityData({ ...securityData, currentPassword: '', newPassword: '', confirmPassword: '' });
      } catch {
        /* changePassword surfaces its own toast */
      } finally {
        setLocalLoading(false);
      }
    };

    return (
      <div className="space-y-5">
        <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Security Settings</p>
        <div
          className="rounded-xl border p-4 space-y-3"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <p className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Change Password</p>
          {[
            { field: 'currentPassword', placeholder: 'Current Password', ref: currentPasswordRef },
            { field: 'newPassword',     placeholder: 'New Password',     ref: newPasswordRef     },
            { field: 'confirmPassword', placeholder: 'Confirm New Password', ref: confirmPasswordRef },
          ].map(({ field, placeholder, ref }) => (
            <div key={field}>
              <input
                ref={ref}
                type="password"
                placeholder={placeholder}
                value={localSecurityData[field]}
                onChange={(e) => handleSecurityInputChange(field, e.target.value)}
                className={inputCls}
                style={inputStyle(false)}
              />
              {field === 'newPassword' && (
                <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textSoft }}>
                  Password should be at least 6 characters long
                </p>
              )}
            </div>
          ))}

          <button
            onClick={handleLocalPasswordChange}
            disabled={localLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
          >
            {localLoading ? (
              <>
                <div className="w-4 h-4 border-b-2 border-white rounded-full animate-spin" />
                Updating…
              </>
            ) : 'Update Password'}
          </button>
        </div>
      </div>
    );
  };

  // ── Sidebar nav items ───────────────────────────────────────────────────────
  const NAV = [
    { id: 'profile',  label: 'Profile',  icon: User         },
    { id: 'security', label: 'Security', icon: Shield       },
    ...(user?.role === 'admin' ? [{ id: 'admin', label: 'Admin', icon: AlertCircle }] : []),
  ];

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
            Account
          </p>
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl" style={{ color: NEUTRAL_COLORS.black }}>
            Settings
          </h1>
          <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            Manage your profile, security, and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div
              className="rounded-xl border p-3"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
            >
              <div className="space-y-1">
                {NAV.map(({ id, label, icon: Icon }) => {
                  const active = activeTab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      className="w-full text-left px-3 py-2.5 rounded-xl flex items-center gap-2.5 text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: active ? SYSTEM_COLORS.primaryTint : 'transparent',
                        color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                      }}
                    >
                      <Icon size={16} />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-3">
            <div
              className="rounded-xl border p-5 sm:p-6"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
            >
              {activeTab === 'profile'  && renderProfileSettings()}
              {activeTab === 'security' && <SecuritySettings />}
              {activeTab === 'admin'    && <AdminSection />}
            </div>
          </div>

        </div>
      </div>

      <ProfilePictureModal
        isOpen={isProfilePictureModalOpen}
        onClose={() => setIsProfilePictureModalOpen(false)}
        onUpload={handleProfilePictureUpload}
      />
    </div>
  );
};

export default SettingsPage;
